import { Link } from "@/components/marketing/elements/link"
import { NoIndexHead } from "@/components/marketing/NoIndexHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MinimalMarketingLayout } from "@/components/marketing/MinimalMarketingLayout"
import { DocumentCentered } from "@/components/marketing/sections/document-centered"
import { legalOperator } from "@/lib/legal-config"
import { isLaunchMode } from "@/lib/launch-config"

export function TermsPage() {
  const Layout = isLaunchMode() ? MinimalMarketingLayout : MarketingLayout

  return (
    <Layout>
      <NoIndexHead />
      <DocumentCentered
        headline="Terms of use"
        subheadline={<p>Last updated: August 2026. By using ArtlessDev during limited availability, you agree to these terms.</p>}
      >
        <div className="space-y-8 text-sm/7 text-mauve-700 dark:text-mauve-400">
          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Provider</h2>
            <p className="mt-3">
              {legalOperator.name} (CIF: {legalOperator.cif}), {legalOperator.address}.
            </p>
            <p className="mt-3">
              Contact:{" "}
              <Link href={`mailto:${legalOperator.email}`}>{legalOperator.email}</Link>
            </p>
            <p className="mt-3">
              ArtlessDev is developed at Binskea and operated by {legalOperator.name}.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">The service</h2>
            <p className="mt-3">
              ArtlessDev lets teams write and browse structured behavioral scenarios in the app — for developers, QA,
              product owners, and stakeholders. It is offered as a{" "}
              <strong className="font-medium text-mauve-950 dark:text-white">limited-availability</strong> web app. Features, workflows,
              and details may change over time.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Your content</h2>
            <p className="mt-3">
              You keep ownership of scenarios and other content you create in your workspaces. You grant
              {legalOperator.name} permission to host and display that content only to provide the service to you
              and your teammates. You remain responsible for the legality, accuracy, and appropriateness of content you
              create or manage.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Acceptable use</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Do not use ArtlessDev for unlawful, abusive, fraudulent, or infringing purposes.</li>
              <li>Do not interfere with service availability, integrity, or security.</li>
              <li>Do not attempt unauthorized access to systems, accounts, or data related to the service.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Limited availability and billing</h2>
            <p className="mt-3">
              While availability is limited, the service is free. Paid plans are not charged yet. We will give notice before billing
              launches. Higher limits or paid-tier features during limited availability are available by email request at no cost.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Intellectual property</h2>
            <p className="mt-3">
              ArtlessDev software, branding, documentation, and related materials are protected by intellectual property
              laws. No rights are granted except those strictly necessary to use the service under these terms.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Disclaimer and limitation of liability</h2>
            <p className="mt-3">
              The service is provided on an &quot;as is&quot; and &quot;as available&quot; basis. To the extent permitted
              by law, {legalOperator.name} disclaims warranties not expressly stated and is not liable for indirect,
              incidental, special, or consequential damages arising from use or inability to use the service. Our total
              liability is limited to what you paid us in the past twelve months (zero while availability is limited).
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Termination</h2>
            <p className="mt-3">
              You may stop using the service at any time. We may suspend or close accounts that violate these terms or
              threaten the service.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Changes</h2>
            <p className="mt-3">
              We may update the service, these terms, and related policies from time to time. The updated version will be
              published with a revised date.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Applicable law and jurisdiction</h2>
            <p className="mt-3">
              These terms are governed by Spanish law. Disputes are subject to the courts of Barcelona, Spain, unless
              mandatory legal rules provide otherwise. If mandatory consumer protection or local laws apply in your
              country, those mandatory rules continue to apply where required.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Contact</h2>
            <p className="mt-3">
              Questions about these terms:{" "}
              <Link href={`mailto:${legalOperator.email}`}>{legalOperator.email}</Link>. See also our{" "}
              <Link href="/legal/privacy">privacy policy</Link>.
            </p>
          </section>
        </div>
      </DocumentCentered>
    </Layout>
  )
}
